/**
 * Exportação de tabelas para CSV, do lado do navegador.
 *
 * Não depende de endpoint: o que a tela exporta é exatamente o que ela mostra,
 * já filtrado. Um endpoint de exportação devolveria o conjunto inteiro e
 * divergiria do que está na frente do usuário.
 */

/**
 * Escapa um valor para CSV.
 *
 * Campo que contém o separador, aspas ou quebra de linha precisa vir entre
 * aspas, e as aspas internas duplicadas. Sem isso, um nome como
 * `Silva, Maria` quebra a coluna e desloca a linha inteira.
 */
function escapar(valor: unknown): string {
  if (valor === null || valor === undefined) return '';
  const texto = String(valor);
  if (/[",;\n\r]/.test(texto)) return `"${texto.replace(/"/g, '""')}"`;
  return texto;
}

export interface ColunaCsv<T> {
  cabecalho: string;
  valor: (linha: T) => unknown;
}

/**
 * Monta o CSV e dispara o download.
 *
 * Duas escolhas de compatibilidade com o Excel em português, que é onde estes
 * arquivos vão ser abertos:
 *
 * - separador `;` em vez de `,`. O Excel em configuração brasileira usa a
 *   vírgula como separador decimal e o ponto e vírgula como separador de
 *   coluna; com vírgula, ele joga a linha toda numa célula só.
 * - BOM no início. Sem ele o Excel lê o arquivo como ANSI e "Messejana" vira
 *   texto corrompido.
 */
export function exportarCsv<T>(
  nomeArquivo: string,
  colunas: ColunaCsv<T>[],
  linhas: T[],
): void {
  const conteudo = [
    colunas.map((c) => escapar(c.cabecalho)).join(';'),
    ...linhas.map((l) => colunas.map((c) => escapar(c.valor(l))).join(';')),
  ].join('\r\n');

  const blob = new Blob(['﻿' + conteudo], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `${nomeArquivo}-${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Sem revogar, o blob fica retido em memória enquanto a aba estiver aberta.
  URL.revokeObjectURL(url);
}
